import { useEffect, useRef, useState } from 'react';

const ITEM_H = 40; // 각 항목 높이(px)
const VISIBLE = 5; // 보이는 항목 수(홀수) — 가운데가 선택
const PAD_ROWS = (VISIBLE - 1) / 2;

interface TimeWheelProps {
  hour: number; // 0-23
  minute: number; // 0-59 (1분 단위)
  onChange: (hour: number, minute: number) => void;
  /** 직접 입력(시간 타이핑) 토글 노출 여부 */
  allowManual?: boolean;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * 핸드폰 알람식 세로 휠 시간 선택 — 시/분을 위아래로 돌려 고른다(1분 단위).
 * 터치는 네이티브 스크롤, 데스크톱은 마우스 드래그/휠. '직접 입력'으로 타이핑도 가능.
 */
export function TimeWheel({ hour, minute, onChange, allowManual = true }: TimeWheelProps) {
  const [manual, setManual] = useState(false);
  // 시/분을 내부 상태로 독립 관리 — 두 휠이 같은 틱에 바뀌어도 서로 덮어쓰지 않게 한다.
  const [h, setH] = useState(hour);
  const [m, setM] = useState(minute);
  useEffect(() => setH(hour), [hour]);
  useEffect(() => setM(minute), [minute]);
  useEffect(() => {
    if (h !== hour || m !== minute) onChange(h, m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [h, m]);

  if (manual) {
    return (
      <div>
        <div className="mb-2 flex items-center justify-end">
          <button
            type="button"
            onClick={() => setManual(false)}
            className="text-small font-semibold text-ink-faint underline-offset-2 hover:underline focus-visible:outline-none focus-visible:underline"
          >
            돌려서 선택
          </button>
        </div>
        <input
          type="time"
          value={`${pad2(hour)}:${pad2(minute)}`}
          step={60}
          aria-label="시간 직접 입력"
          onChange={(e) => {
            const [h, m] = e.target.value.split(':').map((v) => Number.parseInt(v, 10));
            if (Number.isFinite(h) && Number.isFinite(m)) onChange(h, m);
          }}
          className="w-full rounded-xl border border-surface-border bg-surface px-3.5 py-2.5 text-feature font-semibold tabular-nums text-ink focus-visible:outline-none focus-visible:shadow-focus-ring"
        />
      </div>
    );
  }

  return (
    <div>
      {allowManual ? (
        <div className="mb-2 flex items-center justify-end">
          <button
            type="button"
            onClick={() => setManual(true)}
            className="text-small font-semibold text-ink-faint underline-offset-2 hover:underline focus-visible:outline-none focus-visible:underline"
          >
            직접 입력
          </button>
        </div>
      ) : null}
      <div className="relative flex items-stretch justify-center gap-2">
        {/* 가운데 선택 밴드 */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 z-10 rounded-xl bg-surface-muted/60 ring-1 ring-surface-border"
          style={{ top: PAD_ROWS * ITEM_H, height: ITEM_H }}
        />
        <WheelColumn values={HOURS} selected={h} onSelect={setH} ariaLabel="시" suffix="시" />
        <span className="z-20 flex items-center text-feature font-semibold text-ink-faint">:</span>
        <WheelColumn values={MINUTES} selected={m} onSelect={setM} ariaLabel="분" suffix="분" />
      </div>
    </div>
  );
}

interface WheelColumnProps {
  values: number[];
  selected: number;
  onSelect: (value: number) => void;
  ariaLabel: string;
  suffix: string;
}

function WheelColumn({ values, selected, onSelect, ariaLabel, suffix }: WheelColumnProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const committedRef = useRef(selected);
  const rafRef = useRef<number | null>(null);
  const [activeIdx, setActiveIdx] = useState(() => Math.max(0, values.indexOf(selected)));

  // 마운트 시 선택값 위치로 즉시 이동
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTop = Math.max(0, values.indexOf(selected)) * ITEM_H;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 외부에서 selected가 바뀐 경우에만 스크롤 재정렬 (내부 스크롤로 인한 변경은 무시 → 떨림 방지)
  useEffect(() => {
    if (selected === committedRef.current) return;
    committedRef.current = selected;
    const el = ref.current;
    if (!el) return;
    const idx = Math.max(0, values.indexOf(selected));
    setActiveIdx(idx);
    el.scrollTo({ top: idx * ITEM_H, behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  function handleScroll() {
    const el = ref.current;
    if (!el) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const idx = Math.max(0, Math.min(values.length - 1, Math.round(el.scrollTop / ITEM_H)));
      setActiveIdx(idx);
      const value = values[idx];
      if (value !== committedRef.current) {
        committedRef.current = value;
        onSelect(value);
      }
    });
  }

  // 데스크톱 마우스 세로 드래그 → 스크롤 (터치는 네이티브)
  const drag = useRef<{ id: number; startY: number; startTop: number } | null>(null);
  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.pointerType === 'touch') return;
    const el = ref.current;
    if (!el) return;
    drag.current = { id: e.pointerId, startY: e.clientY, startTop: el.scrollTop };
  }
  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const el = ref.current;
    const d = drag.current;
    if (!el || !d || d.id !== e.pointerId) return;
    if (drag.current && !el.hasPointerCapture(e.pointerId)) {
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
    }
    el.scrollTop = d.startTop - (e.clientY - d.startY);
  }
  function onPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    const el = ref.current;
    if (!el || !drag.current || drag.current.id !== e.pointerId) return;
    drag.current = null;
    const idx = Math.max(0, Math.min(values.length - 1, Math.round(el.scrollTop / ITEM_H)));
    el.scrollTo({ top: idx * ITEM_H, behavior: 'smooth' });
    try {
      el.releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
  }

  return (
    <div
      ref={ref}
      role="listbox"
      aria-label={ariaLabel}
      onScroll={handleScroll}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      className="scrollbar-none snap-y snap-mandatory overflow-y-scroll overscroll-contain"
      style={{ height: VISIBLE * ITEM_H, touchAction: 'pan-y' }}
    >
      {/* 위 여백 — 첫 항목이 가운데로 올 수 있게 */}
      <div style={{ height: PAD_ROWS * ITEM_H }} aria-hidden="true" />
      {values.map((v, i) => {
        const dist = Math.abs(i - activeIdx);
        const opacity = dist === 0 ? 1 : dist === 1 ? 0.55 : dist === 2 ? 0.28 : 0.14;
        return (
          <button
            key={v}
            type="button"
            role="option"
            aria-selected={v === selected}
            tabIndex={-1}
            onClick={() => {
              committedRef.current = v;
              setActiveIdx(i);
              onSelect(v);
              ref.current?.scrollTo({ top: i * ITEM_H, behavior: 'smooth' });
            }}
            className={`flex w-14 snap-center items-center justify-center text-feature tabular-nums transition-[opacity,font-weight] ${
              dist === 0 ? 'font-semibold text-ink' : 'font-medium text-ink'
            }`}
            style={{ height: ITEM_H, opacity }}
          >
            {pad2(v)}
            <span className="ml-0.5 text-tag text-ink-faint">{suffix}</span>
          </button>
        );
      })}
      <div style={{ height: PAD_ROWS * ITEM_H }} aria-hidden="true" />
    </div>
  );
}
